import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CredentialItem = ({ 
  item, 
  isSelected, 
  onSelect, 
  onEdit, 
  onShare, 
  onCopy,
  viewMode = 'list' 
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState(null);

  const getItemIcon = (type) => {
    switch (type) {
      case 'password': return 'Key';
      case 'api-key': return 'Code';
      case 'ssh-key': return 'Terminal';
      case 'database': return 'Database';
      case 'secure-note': return 'FileText';
      default: return 'Lock';
    }
  };

  const getStrengthColor = (strength) => {
    switch (strength) {
      case 'strong': return 'text-success';
      case 'medium': return 'text-warning';
      case 'weak': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const getStrengthIcon = (strength) => {
    switch (strength) {
      case 'strong': return 'Shield';
      case 'medium': return 'ShieldAlert';
      case 'weak': return 'ShieldX';
      default: return 'Shield';
    }
  };

  const handleCopy = async (field, value) => {
    try {
      await navigator.clipboard?.writeText(value);
      setCopied(field);
      onCopy?.(field, value);
      setTimeout(() => setCopied(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const formatDate = (date) => {
    return new Date(date)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (viewMode === 'grid') {
    return (
      <div className={`bg-card border border-border rounded-lg p-4 transition-smooth hover:shadow-card ${
        isSelected ? 'ring-2 ring-primary' : ''
      }`}>
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={(e) => onSelect(item?.id, e?.target?.checked)}
              className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
            />
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              item?.type === 'password' ? 'bg-blue-100 text-blue-600' :
              item?.type === 'api-key' ? 'bg-green-100 text-green-600' :
              item?.type === 'ssh-key' ? 'bg-purple-100 text-purple-600' :
              item?.type === 'database'? 'bg-orange-100 text-orange-600' : 'bg-gray-100 text-gray-600'
            }`}>
              <Icon name={getItemIcon(item?.type)} size={20} />
            </div>
          </div>
          
          <div className="flex items-center space-x-1">
            {item?.isShared && (
              <Icon name="Share2" size={14} className="text-accent" />
            )}
            {item?.hasAttachments && (
              <Icon name="Paperclip" size={14} className="text-muted-foreground" />
            )}
            {item?.isFavorite && (
              <Icon name="Star" size={14} className="text-warning fill-current" />
            )}
          </div>
        </div>
        <div className="mb-3">
          <h3 className="font-medium text-foreground truncate mb-1">{item?.name}</h3>
          <p className="text-sm text-muted-foreground truncate">{item?.url || item?.description}</p>
        </div>
        <div className="flex items-center justify-between text-xs text-muted-foreground mb-3">
          <span>Modified {formatDate(item?.lastModified)}</span>
          {item?.passwordStrength && (
            <div className="flex items-center space-x-1">
              <Icon 
                name={getStrengthIcon(item?.passwordStrength)} 
                size={12} 
                className={getStrengthColor(item?.passwordStrength)} 
              />
              <span className={getStrengthColor(item?.passwordStrength)}>
                {item?.passwordStrength}
              </span>
            </div>
          )}
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-1">
            {item?.tags?.slice(0, 2)?.map(tag => (
              <span key={tag} className="px-2 py-1 bg-muted text-xs rounded-full">
                {tag}
              </span>
            ))}
            {item?.tags?.length > 2 && (
              <span className="text-xs text-muted-foreground">+{item?.tags?.length - 2}</span>
            )}
          </div>
          
          <div className="flex items-center space-x-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleCopy('username', item?.username)}
              className="w-6 h-6"
            >
              <Icon name={copied === 'username' ? 'Check' : 'User'} size={12} />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleCopy('password', item?.password)}
              className="w-6 h-6"
            >
              <Icon name={copied === 'password' ? 'Check' : 'Copy'} size={12} />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onEdit(item)}
              className="w-6 h-6"
            >
              <Icon name="Edit" size={12} />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-card border-b border-border p-4 transition-smooth hover:bg-muted ${
      isSelected ? 'bg-accent/10' : ''
    }`}>
      <div className="flex items-center space-x-4">
        {/* Selection Checkbox */}
        <input
          type="checkbox"
          checked={isSelected}
          onChange={(e) => onSelect(item?.id, e?.target?.checked)}
          className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
        />

        {/* Item Icon */}
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
          item?.type === 'password' ? 'bg-blue-100 text-blue-600' :
          item?.type === 'api-key' ? 'bg-green-100 text-green-600' :
          item?.type === 'ssh-key' ? 'bg-purple-100 text-purple-600' :
          item?.type === 'database'? 'bg-orange-100 text-orange-600' : 'bg-gray-100 text-gray-600'
        }`}>
          <Icon name={getItemIcon(item?.type)} size={20} />
        </div>

        {/* Item Details */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2 mb-1">
            <h3 className="font-medium text-foreground truncate">{item?.name}</h3>
            {item?.isFavorite && (
              <Icon name="Star" size={14} className="text-warning fill-current" />
            )}
            {item?.isShared && (
              <Icon name="Share2" size={14} className="text-accent" />
            )}
            {item?.hasAttachments && (
              <Icon name="Paperclip" size={14} className="text-muted-foreground" />
            )}
          </div>
          
          <div className="flex items-center space-x-4 text-sm text-muted-foreground">
            <span className="truncate">{item?.url || item?.description}</span>
            {item?.username && (
              <span className="truncate">@{item?.username}</span>
            )}
          </div>
        </div>

        {/* Password Field */}
        {item?.password && (
          <div className="flex items-center space-x-2 min-w-0 w-48">
            <div className="flex-1 font-mono text-sm">
              {showPassword ? item?.password : '••••••••••••'}
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowPassword(!showPassword)}
              className="w-6 h-6 flex-shrink-0"
            >
              <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={14} />
            </Button>
          </div>
        )}

        {/* Security Indicators */}
        <div className="flex items-center space-x-3">
          {item?.passwordStrength && (
            <div className="flex items-center space-x-1">
              <Icon 
                name={getStrengthIcon(item?.passwordStrength)} 
                size={16} 
                className={getStrengthColor(item?.passwordStrength)} 
              />
            </div>
          )}
          
          {item?.breachStatus && (
            <Icon 
              name="AlertTriangle" 
              size={16} 
              className="text-error" 
              title="Found in data breach"
            />
          )}
          
          {item?.needsRotation && (
            <Icon 
              name="RotateCcw" 
              size={16} 
              className="text-warning" 
              title="Needs rotation"
            />
          )}
        </div>

        {/* Tags */}
        <div className="flex items-center space-x-1 min-w-0 w-32">
          {item?.tags?.slice(0, 2)?.map(tag => (
            <span key={tag} className="px-2 py-1 bg-muted text-xs rounded-full truncate">
              {tag}
            </span>
          ))}
          {item?.tags?.length > 2 && (
            <span className="text-xs text-muted-foreground">+{item?.tags?.length - 2}</span>
          )}
        </div>

        {/* Last Modified */}
        <div className="text-sm text-muted-foreground w-24 flex-shrink-0">
          {formatDate(item?.lastModified)}
        </div>

        {/* Actions */}
        <div className="flex items-center space-x-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => handleCopy('username', item?.username)}
            className="w-8 h-8"
            title="Copy username"
          >
            <Icon name={copied === 'username' ? 'Check' : 'User'} size={16} />
          </Button>
          
          <Button
            variant="ghost"
            size="icon"
            onClick={() => handleCopy('password', item?.password)}
            className="w-8 h-8"
            title="Copy password"
          >
            <Icon name={copied === 'password' ? 'Check' : 'Copy'} size={16} />
          </Button>
          
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onShare(item)}
            className="w-8 h-8"
            title="Share item"
          >
            <Icon name="Share2" size={16} />
          </Button>
          
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onEdit(item)}
            className="w-8 h-8"
            title="Edit item"
          >
            <Icon name="Edit" size={16} />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CredentialItem;